import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, type ItemAlmox, type SolicitacaoAlmox } from "../api";
import { statusTone } from "../format";
import ErrorState from "./ErrorState";
import SolicitarInsumoModal from "./SolicitarInsumoModal";
import { IconSearch, IconTruck, IconRefresh } from "../icons";

interface Props {
  user: string; // usuário logado (filtra "minhas solicitações")
}

type Aba = "insumos" | "solicitacoes";

/**
 * Tela do Almoxarifado dentro do Operacional: consulta (somente leitura) do
 * estoque de insumos e solicitação de separação. Os dados vêm do serviço do
 * Almoxarifado (porta 4004) via proxy do backend — nada é gravado localmente.
 */
export default function AlmoxarifadoView({ user }: Props) {
  const [aba, setAba] = useState<Aba>("insumos");
  const [itens, setItens] = useState<ItemAlmox[]>([]);
  const [solic, setSolic] = useState<SolicitacaoAlmox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [search, setSearch] = useState("");
  const [solicitar, setSolicitar] = useState<ItemAlmox | null>(null);
  const [soMinhas, setSoMinhas] = useState(true);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (aba === "insumos") {
        setItens(await api.almox.listItens());
      } else {
        setSolic(await api.almox.listSolicitacoes(soMinhas ? user : undefined));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, [aba, soMinhas, user]);

  useEffect(() => {
    setSearch("");
    carregar();
  }, [carregar]);

  const itensFiltrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return itens;
    return itens.filter((i) =>
      [i.descricao, i.categoria, i.codigo, i.localizacao].some((v) =>
        String(v ?? "").toLowerCase().includes(q),
      ),
    );
  }, [itens, search]);

  if (error)
    return <ErrorState message={error.message} code={error.code} onRetry={carregar} />;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div className="almox-tabs">
          <button
            className={`almox-tab ${aba === "insumos" ? "active" : ""}`}
            onClick={() => setAba("insumos")}
          >
            Insumos
          </button>
          <button
            className={`almox-tab ${aba === "solicitacoes" ? "active" : ""}`}
            onClick={() => setAba("solicitacoes")}
          >
            Minhas solicitações
          </button>
        </div>

        {aba === "insumos" ? (
          <div className="search-wrap">
            <IconSearch size={17} />
            <input
              className="search"
              placeholder="Buscar insumo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        ) : (
          <label className="almox-check">
            <input type="checkbox" checked={soMinhas} onChange={(e) => setSoMinhas(e.target.checked)} />
            Apenas as minhas
          </label>
        )}

        <div className="toolbar-right">
          <span className="badge">
            {aba === "insumos" ? `${itensFiltrados.length} insumo(s)` : `${solic.length} solicitação(ões)`}
          </span>
          <button className="btn-secondary" onClick={carregar} title="Atualizar">
            <IconRefresh size={16} /> Atualizar
          </button>
        </div>
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando…</div>
        ) : aba === "insumos" ? (
          itensFiltrados.length === 0 ? (
            <div className="empty">Nenhum insumo encontrado.</div>
          ) : (
            <table className="table table-compact">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Insumo</th>
                  <th>Categoria</th>
                  <th>Disponível</th>
                  <th>Local</th>
                  <th>Situação</th>
                  <th className="col-actions">Ações</th>
                </tr>
              </thead>
              <tbody>
                {itensFiltrados.map((i) => (
                  <tr key={i.id}>
                    <td>{i.codigo || "—"}</td>
                    <td>{i.descricao}</td>
                    <td>{i.categoria || "—"}</td>
                    <td>
                      <strong>{i.saldo}</strong> {i.unidade}
                    </td>
                    <td>{i.localizacao || "—"}</td>
                    <td>
                      {i.situacao ? (
                        <span className={`pill pill-${statusTone(i.situacao)}`}>{i.situacao}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="col-actions">
                      <button
                        className="btn-primary btn-sm"
                        onClick={() => setSolicitar(i)}
                        title="Solicitar separação ao Almoxarifado"
                      >
                        <IconTruck size={15} /> Solicitar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : solic.length === 0 ? (
          <div className="empty">Nenhuma solicitação ainda. Use a aba "Insumos" para solicitar.</div>
        ) : (
          <table className="table table-compact">
            <thead>
              <tr>
                <th>Insumo</th>
                <th>Qtd.</th>
                <th>Prioridade</th>
                <th>Status</th>
                <th>Solicitado em</th>
                <th>Solicitante</th>
              </tr>
            </thead>
            <tbody>
              {solic.map((s) => (
                <tr key={s.id}>
                  <td>{s.item_descricao || `#${s.item_id}`}</td>
                  <td>{s.quantidade}</td>
                  <td>
                    <span className={`pill pill-${statusTone(s.prioridade)}`}>{s.prioridade}</span>
                  </td>
                  <td>
                    <span className={`pill pill-${statusTone(s.status)}`}>{s.status}</span>
                  </td>
                  <td>{s.data_solicitacao ? new Date(s.data_solicitacao).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "—"}</td>
                  <td>{s.solicitante || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {solicitar && (
        <SolicitarInsumoModal
          item={solicitar}
          onClose={() => setSolicitar(null)}
          onSolicitada={() => {
            // Mostra a nova solicitação ao trocar de aba/atualizar.
            if (aba === "solicitacoes") carregar();
          }}
        />
      )}
    </div>
  );
}
